import React from 'react'
import {
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { grey } from '@mui/material/colors'
import PopoverContainer from './PopoverContainer'
import useDropdowns from '../../hooks/api/useDropdowns'
import { ArrowDropDown, ArrowDropUp } from '@mui/icons-material'
import useIsMobile from '../../hooks/useIsMobile'

const sum = (items, key) => {
  return items.reduce(function (a, b) {
    return (a ? Number(a) : 0) + (b[key] ? Number(b[key]) : 0)
  }, 0)
}

const SortIcon = ({ column, sortColumn, sortDirIndex }) => {
  const icons = [null, <ArrowDropDown />, <ArrowDropUp />]

  if (column.key !== sortColumn) return null

  return icons[sortDirIndex]
}

const DataTable = ({
  data,
  selectableColumns,
  updateSort,
  sortColumn,
  sortDirIndex,
  selectedColumnKeys,
  isLoading,
  customFields,
}) => {
  const customFieldsAsColumns = customFields.map((c) => {
    return {
      label: c.name,
      key: `CUSTOM-${c.id}`,
      getValue: (d) => {
        const valueOrId = (d.customFields || {})[c.id]

        let value = valueOrId

        if (c.typeId === 4 && valueOrId) {
          value = c.options.find((o) => o.id === valueOrId)?.name
        }

        if (c.typeId === 5 && valueOrId) {
          const options = c.options.filter((o) =>
            (valueOrId || []).includes(o.id)
          )

          value = options.map((o) => o.name).join(', ')
        }

        return value
      },
      sum: c.typeId === 2,
    }
  })

  const selectedColumns = [
    ...selectableColumns,
    ...customFieldsAsColumns,
  ].filter((c) => selectedColumnKeys.includes(c.key))

  const { dropdownsSummary } = useDropdowns({ noFallBack: true })

  const getByMultipleKeys = (item, keys) => {
    const firstPopulatedKey = keys.find((k) => item[k])
    return item[firstPopulatedKey]
  }

  const isMobile = useIsMobile()

  return (
    <Table
      sx={{
        borderCollapse: 'separate',
        backgroundColor: 'white',
        minWidth: `${selectedColumns.length * 100}px`,
      }}
    >
      <TableHead sx={{ zIndex: 2 }}>
        <TableRow>
          {selectedColumns.map((c, i) => {
            return (
              <TableCell
                key={`th-${i}`}
                sx={{
                  minWidth: c.width || '100px',
                  width: c.width || null,
                  cursor: 'pointer',
                  position: 'sticky',
                  border: `1px solid ${grey[300]}`,

                  top: 0,
                  backgroundColor: 'white',
                  zIndex: 1,

                  ...(c.key === 'name'
                    ? {
                        left: 0,
                        zIndex: 2,
                      }
                    : {}),
                }}
                onClick={() => {
                  updateSort(c.key)
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                  }}
                >
                  <Box sx={{ display: 'flex' }}>
                    <Box
                      sx={{
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                      }}
                    >
                      {!c.hideInHeader && (
                        <>
                          <span>{c.label}</span>{' '}
                        </>
                      )}
                    </Box>

                    <SortIcon
                      column={c}
                      sortColumn={sortColumn}
                      sortDirIndex={sortDirIndex}
                    />
                  </Box>
                </Box>
              </TableCell>
            )
          })}

          {/* Popover column */}
          {!isMobile && (
            <TableCell
              sx={{
                border: '1px solid rgba(224, 224, 224, 1)',
              }}
            />
          )}
        </TableRow>

        {/* TOTALS ROW */}
        <TableRow>
          {selectedColumns.map((c, i) => {
            return (
              <TableCell
                key={i}
                sx={{
                  border: '1px solid rgba(224, 224, 224, 1)',
                  textAlign: 'center',
                  ...(c.key === 'name'
                    ? {
                        textAlign: 'left',
                        position: 'sticky',
                        left: 0,
                        backgroundColor: 'white',
                        zIndex: 1,
                      }
                    : {}),
                }}
              >
                {c.key === 'name' && <b>TOTALS</b>}
                {c.sum && (
                  <Typography>
                    {c.startAdornment || ''}
                    {sum(data, c.key)}
                    {c.endAdornment || ''}
                  </Typography>
                )}
              </TableCell>
            )
          })}
          {!isMobile && (
            <TableCell sx={{ border: '1px solid rgba(224, 224, 224, 1)' }} />
          )}
        </TableRow>
      </TableHead>

      <TableBody sx={{ position: 'relative' }}>
        {isLoading && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              backgroundColor: 'white',
              position: 'absolute',
              py: 3,
              width: '100%',
            }}
          >
            <CircularProgress />
          </Box>
        )}
        {!isLoading &&
          data.map((d, i) => {
            return (
              <>
                <TableRow key={`tr-${i}`}>
                  {selectedColumns.map((c, ci) => {
                    const key = `td-${ci}`
                    if (c.getDisplay) return c.getDisplay(d, key)

                    const value = c.getValue
                      ? c.getValue(d)
                      : c.keys
                      ? getByMultipleKeys(d, c.keys)
                      : d[c.key]

                    return (
                      <TableCell
                        key={key}
                        sx={{
                          minWidth: '100px',
                          textAlign: 'center',
                          border: '1px solid rgba(224, 224, 224, 1)',
                          borderCollapse: 'collapse',
                        }}
                      >
                        {value || value === 0 ? (
                          `${c.startAdornment || ''}${value}${
                            c.endAdornment || ''
                          }`
                        ) : (
                          <Typography
                            sx={{ color: grey[600], fontSize: '14px' }}
                          >
                            <i>(None)</i>
                          </Typography>
                        )}
                      </TableCell>
                    )
                  })}
                  {!isMobile && (
                    <PopoverContainer
                      dropdowns={dropdownsSummary}
                      customerId={d.id}
                    />
                  )}
                </TableRow>
              </>
            )
          })}
      </TableBody>
    </Table>
  )
}

export default DataTable
